import React, { useEffect, useState } from 'react';
import { Button, Input, SideModal, Typography } from 'design_system/src';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import { GeofenceStatus } from 'models/geofence/enums/geofence.enum';
import {
	CreateGeofenceDto,
	Geofence,
} from 'models/geofence/interfaces/geofence.interface';
import {
	createGeofence,
	updateGeofence,
} from 'models/geofence/controllers/geofence.controller';
import { fetchCompanies } from 'models/user-management/controllers/companies.controller';
import { Company } from 'models/companies/interfaces/companies.interface';

interface EditGeofenceProps {
	geofence?: Geofence;
	onClose: () => void;
	onSave: () => void;
}

type GeofenceFormState = {
	companyId: string;
	name: string;
	status: GeofenceStatus;
	latitude: string;
	longitude: string;
	radiusMeters: string;
};

function EditGeofence({ geofence, onClose, onSave }: EditGeofenceProps) {
	const [open, setOpen] = useState(true);
	const [loading, setLoading] = useState(false);
	const [companies, setCompanies] = useState<Company[]>([]);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [formData, setFormData] = useState<GeofenceFormState>({
		companyId: '',
		name: '',
		status: GeofenceStatus.ACTIVE,
		latitude: '0',
		longitude: '0',
		radiusMeters: '100',
	});

	const handleClose = (value: boolean) => {
		setOpen(value);
		if (!value) onClose();
	};

	useEffect(() => {
		fetchCompanies()
			.then((data) => setCompanies(Array.isArray(data) ? data : []))
			.catch(() => setCompanies([]));
	}, []);

	useEffect(() => {
		if (geofence) {
			setFormData({
				companyId: geofence.company ?? '',
				name: geofence.name ?? '',
				status: geofence.status ?? GeofenceStatus.ACTIVE,
				latitude: String(geofence.center?.coordinates?.[1] ?? 0),
				longitude: String(geofence.center?.coordinates?.[0] ?? 0),
				radiusMeters: String(geofence.radiusMeters ?? 100),
			});
		} else {
			setFormData((prev) => ({ ...prev, companyId: '' }));
		}
	}, [geofence]);

	const handleChange = (field: keyof GeofenceFormState, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => {
				const next = { ...prev };
				delete next[field];
				return next;
			});
		}
	};

	const validateForm = () => {
		const newErrors: Record<string, string> = {};
		if (!formData.companyId?.trim()) {
			newErrors.companyId = 'Company is required';
		}
		if (!formData.name?.trim()) newErrors.name = 'Name is required';
		if (
			formData.latitude.trim() === '' ||
			Number.isNaN(Number(formData.latitude))
		)
			newErrors.latitude = 'Latitude is required';
		if (
			formData.longitude.trim() === '' ||
			Number.isNaN(Number(formData.longitude))
		)
			newErrors.longitude = 'Longitude is required';
		if (
			formData.radiusMeters.trim() === '' ||
			Number.isNaN(Number(formData.radiusMeters)) ||
			Number(formData.radiusMeters) <= 0
		)
			newErrors.radiusMeters = 'Radius must be greater than 0';
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			toasterNotification({
				msg: 'Please fill in all required fields',
				toasterType: toasterOptions.error,
			});
			return;
		}

		setLoading(true);
		try {
			const payload: CreateGeofenceDto = {
				companyId: formData.companyId,
				name: formData.name,
				status: formData.status,
				latitude: Number(formData.latitude),
				longitude: Number(formData.longitude),
				radiusMeters: Number(formData.radiusMeters),
			};

			if (geofence) {
				await updateGeofence(geofence._id, payload);
				toasterNotification({
					msg: 'Geofence updated successfully',
					toasterType: toasterOptions.success,
				});
			} else {
				await createGeofence(payload);
				toasterNotification({
					msg: 'Geofence created successfully',
					toasterType: toasterOptions.success,
				});
			}
			onSave();
		} catch (error: unknown) {
			toasterNotification({
				msg: extractErrorMessage(error) || 'Error saving geofence',
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<SideModal open={open} setOpen={handleClose} onClose={onClose}>
			<div style={{ padding: '24px', minWidth: '500px' }}>
				<Typography
					scale="heading4"
					weight="600"
					style={{ marginBottom: '24px' }}
				>
					{geofence ? 'Edit Geofence' : 'Create Geofence'}
				</Typography>

				<Input
					name="companyId"
					type="select"
					title="Company"
					value={formData.companyId}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
						handleChange('companyId', e.target.value)
					}
					options={companies.map((c) => ({ value: c._id, label: c.name }))}
					disabled={!!geofence}
				/>
				{errors.companyId && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: 4 }}
					>
						{errors.companyId}
					</Typography>
				)}

				<Input
					name="name"
					type="text"
					title="Name"
					value={formData.name}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('name', e.target.value)
					}
					required
				/>
				{errors.name && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: 4 }}
					>
						{errors.name}
					</Typography>
				)}

				<Input
					name="status"
					type="select"
					title="Status"
					value={formData.status || GeofenceStatus.ACTIVE}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
						handleChange('status', e.target.value as GeofenceStatus)
					}
					options={[
						{ value: GeofenceStatus.ACTIVE, label: 'Active' },
						{ value: GeofenceStatus.INACTIVE, label: 'Inactive' },
					]}
				/>

				<Input
					name="latitude"
					type="number"
					title="Latitude"
					value={formData.latitude}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('latitude', e.target.value)
					}
					required
				/>
				{errors.latitude && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: 4 }}
					>
						{errors.latitude}
					</Typography>
				)}

				<Input
					name="longitude"
					type="number"
					title="Longitude"
					value={formData.longitude}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('longitude', e.target.value)
					}
					required
				/>
				{errors.longitude && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: 4 }}
					>
						{errors.longitude}
					</Typography>
				)}

				<Input
					name="radiusMeters"
					type="number"
					title="Radius (meters)"
					value={formData.radiusMeters}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('radiusMeters', e.target.value)
					}
					required
				/>
				{errors.radiusMeters && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: 4 }}
					>
						{errors.radiusMeters}
					</Typography>
				)}

				<div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
					<Button variant="principal" onClick={handleSubmit} disabled={loading}>
						{loading ? 'Saving...' : 'Save'}
					</Button>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
				</div>
			</div>
			<div />
		</SideModal>
	);
}

export default EditGeofence;
